import { Heading, Input, Link, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

import { resendVerificationEmail } from "../api/auth";
import { Button } from "../components/button";
import { Field } from "../components/field";
import LoginShell from "../components/login-shell";
import { PasswordInput } from "../components/password-input";
import { toaster } from "../components/toaster";
import { useAppDispatch, useAppSelector } from "../hooks";
import { setReturnLocation, signIn } from "../store/authSlice";

interface LoginForm {
  username: string;
  password: string;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { returnLocation } = useAppSelector((s) => s.auth);

  const [isLoading, setIsLoading] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();
  const onSubmit = handleSubmit(async ({ username, password }) => {
    setIsLoading(true);
    try {
      await dispatch(
        signIn({
          username,
          password,
        }),
      ).unwrap();
      dispatch(setReturnLocation(null));
      navigate(returnLocation || "/");
    } catch (e) {
      if (e.code === "unverified") {
        toaster.create({
          type: "error",
          title: "Error",
          description: e.message,
          action: {
            label: "Resend verification e-mail",
            onClick: () => {
              resendVerificationEmail(username)
                .then(() => {
                  toaster.create({
                    type: "success",
                    title: "Verification e-mail sent",
                    description: "Please check your inbox.",
                  });
                })
                .catch(() => {
                  toaster.create({
                    type: "error",
                    title: "Error",
                    description: "Failed to resend verification e-mail.",
                  });
                });
            },
          },
        });
      } else {
        toaster.create({
          type: "error",
          title: "Error",
          description: e.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <LoginShell>
      <Stack color="fg.accent" gap={{ base: "2", md: "3" }} textAlign="center">
        <Heading size={{ base: "2xl", md: "3xl" }}>Sign In</Heading>
      </Stack>
      <form onSubmit={onSubmit}>
        <Stack gap="6">
          <Stack gap="5">
            <Field
              label="Email"
              invalid={!!errors.username}
              errorText={errors.username?.message}
            >
              <Input
                type="email"
                placeholder="johndoe@example.com"
                {...register("username", {
                  required: "Please provide an e-mail address.",
                })}
              />
            </Field>
            <Field
              label="Password"
              invalid={!!errors.password}
              errorText={errors.password?.message}
            >
              <PasswordInput
                {...register("password", {
                  required: "Please provide a password.",
                })}
              />
            </Field>
          </Stack>
          <Stack gap="4">
            <Button loading={isLoading} type="submit">
              Sign In
            </Button>
          </Stack>

          <Text textStyle="sm" color="fg.muted" textAlign="center">
            <Link asChild variant="underline">
              <RouterLink to="/password-reset">Forgot Password?</RouterLink>
            </Link>
          </Text>

          <Text textStyle="sm" color="fg.muted" textAlign="center">
            Don{"'"}t have an account?{" "}
            <Link asChild variant="underline">
              <RouterLink to="/signup">Sign Up</RouterLink>
            </Link>
          </Text>
        </Stack>
      </form>
    </LoginShell>
  );
}
